import { FormEvent, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { getFirebaseServices } from "@/lib/firebase/client";
import { signInWithEmailAndPassword } from "firebase/auth";

export function LoginForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setStatus(null);

    const services = getFirebaseServices();
    if (!services) {
      setStatus("Firebase not configured. Add .env.local with Firebase keys.");
      setLoading(false);
      return;
    }

    const { auth } = services;

    try {
      const userCred = await signInWithEmailAndPassword(auth, email, password);
      
      // Check if email is verified
      if (!userCred.user.emailVerified) {
        await auth.signOut();
        setStatus("Please verify your email before logging in. Check your inbox (and spam folder) for the verification link.");
        setLoading(false);
        return;
      }

      // Redirect to the page they were trying to access, or campaigns
      const redirect = searchParams.get('redirect') || '/campaigns';
      navigate(redirect);
    } catch (err) {
      console.error("Login error:", err);
      if (err instanceof Error) {
        if (err.message.includes("invalid-credential") || err.message.includes("user-not-found")) {
          setStatus("Invalid email or password.");
        } else if (err.message.includes("too-many-requests")) {
          setStatus("Too many failed attempts. Try again later.");
        } else {
          setStatus("Login failed. Check your credentials.");
        }
      } else {
        setStatus("An unexpected error occurred.");
      }
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-md sm:max-w-lg lg:max-w-xl space-y-6 rounded-3xl border border-white/10 bg-zinc-900/70 p-6 sm:p-8 backdrop-blur">
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold text-white">Welcome back</h1>
        <p className="text-sm text-white/60">
          Sign in to access your affiliate dashboard and campaigns.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-white/80">
            Email
          </label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-md border border-white/20 bg-zinc-800/50 px-3 py-2 text-white placeholder-white/40 focus:border-lime-400 focus:outline-none focus:ring-1 focus:ring-lime-400"
            placeholder="you@example.com"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-white/80">
            Password
          </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-md border border-white/20 bg-zinc-800/50 px-3 py-2 pr-10 text-white placeholder-white/40 focus:border-lime-400 focus:outline-none focus:ring-1 focus:ring-lime-400"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-md text-white/70"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
        </div>

        {status && (
          <p className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-400">
            {status}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-md bg-lime-500 px-4 py-2.5 text-sm font-semibold text-black transition hover:bg-lime-400 disabled:opacity-50"
        >
          {loading ? "Signing in..." : "Sign in"}
        </button>
      </form>

      <div className="pt-4 text-center text-sm text-white/60">
        Don&apos;t have an account?{" "}
        <Link to="/auth/signup" className="font-medium text-lime-400 hover:text-lime-300">
          Sign up
        </Link>
      </div>
    </div>
  );
}
